/**
 * Plain-language deficiency description, shared by Attention and the review flow.
 *
 * ── The rounding bug this module was extracted to fix ─────────────────────────────────────────
 * Observed live on dev 2026-08-14, Foster Rd: Attention read
 *   "missing 100% of control mapping ce005 and 4 other categories"
 * while the real figures were detected 1465, included 5. 1460/1465 = 99.66%, and `Math.round`
 * turned that into a flat 100%.
 *
 * In a compliance product that is not a cosmetic rounding artefact — it is a false statement about
 * evidence. "100% missing" says nothing was captured; five records were. The same error exists at
 * the other end: a category missing a handful of rows out of a hundred thousand rounds to 0% and
 * reads as complete when it is not.
 *
 * So the rule here is that the boundary values 0% and 100% are RESERVED for the exact cases, and
 * anything close-but-not-equal is reported as "<1%" or ">99%". A number a reader could act on must
 * never be rounded into a claim the data does not support.
 */

/**
 * Internal category keys are not user-facing language. Unmapped keys fall back to a de-underscored
 * form rather than being hidden — a new category must never silently vanish from a deficiency
 * summary just because this map was not updated.
 */
const CATEGORY_LABELS = {
  observed_events: 'observed events',
  derived_events: 'derived events',
  command_traces: 'command traces',
  incident_candidates: 'incident candidates',
  operational_timeline_observed: 'observed timeline events',
  operational_timeline_derived: 'derived timeline events',
  control_mapping_ce005: 'control mappings',
};

export function categoryLabel(name) {
  return CATEGORY_LABELS[name] || String(name).replace(/_/g, ' ');
}

/** Raw percentage of a category's detected volume that did not make it in. Not for display. */
export function missingPct(cat) {
  const detected = (cat && cat.detected_count) || 0;
  if (!detected) return 0;
  const included = (cat && cat.included_count) || 0;
  return ((detected - included) / detected) * 100;
}

/**
 * Display form. 0% and 100% are claims of exactness and are only ever produced when the underlying
 * counts are exactly that; everything else is bounded away from them.
 */
export function missingLabel(cat) {
  const detected = (cat && cat.detected_count) || 0;
  const included = (cat && cat.included_count) || 0;
  const missing = detected - included;
  if (detected <= 0 || missing <= 0) return '0%';
  if (missing >= detected) return '100%';
  const pct = (missing / detected) * 100;
  if (pct >= 99.5) return '>99%';
  if (pct < 0.5) return '<1%';
  return `${Math.round(pct)}%`;
}

/** Incomplete categories, worst first. */
export function incompleteCategories(completeness) {
  return Object.entries((completeness && completeness.categories) || {})
    .filter(([, c]) => !c.complete)
    .map(([name, c]) => ({ name, label: categoryLabel(name), pct: missingPct(c), display: missingLabel(c) }))
    .sort((a, b) => b.pct - a.pct);
}

/** One short line for a list row — magnitude first, which is what a technician acts on. */
export function summariseDeficiency(completeness) {
  const cats = incompleteCategories(completeness);
  if (!cats.length) return 'incomplete';
  const worst = cats[0];
  if (cats.length === 1) return `missing ${worst.display} of ${worst.label}`;
  return `missing ${worst.display} of ${worst.label} and ${cats.length - 1} other ${
    cats.length - 1 === 1 ? 'category' : 'categories'
  }`;
}

/**
 * The fuller sentence the review screen leads with, naming the two worst categories and the reason.
 * Deliberately a sentence and not a table: the table follows underneath and answers "exactly how
 * much", while this answers "what am I being asked to approve".
 */
export function deficiencySentence(completeness) {
  const cats = incompleteCategories(completeness);
  if (!cats.length) return 'This packet is complete for the period.';
  const named = cats.slice(0, 2).map((c) => `${c.display} of ${c.label}`);
  const rest = cats.length - named.length;
  const list = named.join(' and ')
    + (rest > 0 ? `, and is short in ${rest} further ${rest === 1 ? 'category' : 'categories'}` : '');
  return `This packet is missing ${list} because the volume collected for this period exceeded the `
    + 'limits the generator applies to a single packet.';
}

/**
 * Category → Evidence Explorer search, ONLY where the mapping is honest.
 *
 * Four of the seven completeness categories have a search object_type that means the same thing.
 * Three do not, and are deliberately left unlinked rather than approximated:
 *
 *   operational_timeline_observed  ─┐ both are object_type "timeline" in /ce/search, which cannot
 *   operational_timeline_derived   ─┘ distinguish them. A link would silently return the union, so
 *                                     clicking "observed timeline" would show derived rows too.
 *   control_mapping_ce005            has no search object_type at all.
 *
 * A link that lands on a different set than its label promises is a credibility problem, not a
 * convenience — on a compliance surface especially. Unmapped categories render as plain rows.
 *
 * Even the four that map are a JUMPING-OFF POINT, not a reproduction of the packet: the generator
 * selects by derived_at containment while search matches on window overlap, incident_candidates is
 * counted at cluster level while search returns individual cases, and a capped category by
 * definition holds fewer rows than the store. The explorer says so on arrival.
 */
const CATEGORY_SEARCH_TYPE = {
  observed_events: 'observed_event',
  derived_events: 'derived_event',
  command_traces: 'command_trace',
  incident_candidates: 'incident_case',
};

export function categorySearchType(name) {
  return CATEGORY_SEARCH_TYPE[name] || null;
}

/** Explorer URL for a category, or null when no honest mapping exists. */
export function categorySearchHref(name, { siteId, periodStart, periodEnd } = {}) {
  const type = categorySearchType(name);
  if (!type || !siteId) return null;
  const params = new URLSearchParams({ site: siteId, types: type });
  // datetime-local inputs, which is what the explorer's fields are bound to.
  if (periodStart) params.set('start', String(periodStart).slice(0, 16));
  if (periodEnd) params.set('end', String(periodEnd).slice(0, 16));
  return `/ce/explorer?${params.toString()}`;
}
